import type { CSSProperties } from 'react';
import clsx from 'clsx';
import type { Feed, FeedEntry, UIComponent } from '~/services/layout/index.ts';
import {
  isTextLikeFormType,
  resolveTextInputType,
  type TextInputType,
} from '~/services/plugins/quick-brick-form-screen/types.ts';

export type TextFieldUiComponent = UIComponent & {
  addition_styles?: Record<string, string | number | undefined>;
  data?: Record<string, unknown>;
};

export type TextFieldEntryMetadata = {
  label: string;
  description?: string;
  minLength?: number;
  maxLength?: number;
  required?: boolean;
};

export type TextFieldTitlePosition = 'beside' | 'above';
export type TextFieldSlotPosition = 'above_input' | 'below_input';

export function getTextTransformClass(value: unknown): string {
  if (value === 'uppercase') return 'uppercase';
  if (value === 'lowercase') return 'lowercase';
  if (value === 'capitalize') return 'capitalize';
  return '';
}

export function getTextAlignClass(value: unknown): string {
  if (value === 'center') return 'text-center';
  if (value === 'right') return 'text-right';
  return 'text-left';
}

function getTextLikeEntry(feed?: Feed): FeedEntry | undefined {
  return feed?.entry?.find((item) =>
    isTextLikeFormType(item.extensions?.formType)
  );
}

export function getTextFieldEntryMetadata(
  feed?: Feed
): TextFieldEntryMetadata | null {
  const entry = getTextLikeEntry(feed);
  if (!entry) return null;

  const extensions = entry.extensions ?? {};
  return {
    label: entry.title,
    description:
      typeof extensions.description === 'string'
        ? extensions.description
        : undefined,
    minLength:
      typeof extensions.minLength === 'number'
        ? extensions.minLength
        : undefined,
    maxLength:
      typeof extensions.maxLength === 'number'
        ? extensions.maxLength
        : undefined,
    required: extensions.required === true,
  };
}

export function getResolvedInputType(input: {
  feed?: Feed;
  uiComponent: TextFieldUiComponent;
}): TextInputType {
  const entry = getTextLikeEntry(input.feed);
  return resolveTextInputType({
    formType: entry?.extensions?.formType,
    inputType:
      entry?.extensions?.inputType ??
      input.uiComponent.data?.input_type ??
      input.uiComponent.data?.inputType,
  });
}

export function pickNonEmptyString(input: {
  value: unknown;
  fallback: string;
}): string {
  return typeof input.value === 'string' && input.value.length > 0
    ? input.value
    : input.fallback;
}

/**
 * Returns the error-message container classes for display, asset gap, and alignment.
 */
export function getErrorContainerClassName(input: {
  display: unknown;
  verticalAlignment: unknown;
  hasAsset: boolean;
}): string {
  return clsx(
    'text-field-error-container',
    input.display === 'fit'
      ? 'text-field-error-container-fit'
      : 'text-field-error-container-fill',
    input.hasAsset && 'text-field-error-container-with-asset',
    getErrorAssetAlignmentClass(input.verticalAlignment)
  );
}

/**
 * Returns a leading error asset URL when the asset is enabled and non-empty.
 */
export function getErrorLeadingAssetUrl(input: {
  enabled: unknown;
  asset: unknown;
}): string | undefined {
  if (input.enabled !== true) return undefined;
  if (typeof input.asset !== 'string' || input.asset.length === 0) {
    return undefined;
  }

  return input.asset;
}

/**
 * Maps the error asset vertical alignment to a flex alignment class.
 */
function getErrorAssetAlignmentClass(value: unknown): string {
  if (value === 'center') return 'items-center';
  if (value === 'bottom') return 'items-end';
  return 'items-start';
}

/**
 * Returns a trimmed asset URL. A cleared uploader can leave whitespace,
 * which must not replace an icon.
 */
export function configuredAssetUrl(asset?: string | null): string | undefined {
  if (typeof asset !== 'string') return undefined;

  const trimmed = asset.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

export function getTextFieldInputClassName(input: {
  inputBorderStyle: string;
  hasError: boolean;
}): string {
  return clsx(
    'text-field-input',
    input.inputBorderStyle === 'bottom' && 'text-field-input-border-bottom',
    input.hasError && 'text-field-input-error'
  );
}

export function getAdditionInputStyle(
  additionStyles?: Record<string, string | number | undefined>
): CSSProperties | undefined {
  const additionBorderColor = additionStyles?.borderColor;
  const additionBorderWidth = additionStyles?.borderWidth;

  if (additionBorderColor === undefined && additionBorderWidth === undefined) {
    return undefined;
  }

  return {
    borderColor:
      typeof additionBorderColor === 'string' ? additionBorderColor : undefined,
    borderWidth:
      typeof additionBorderWidth === 'number' ? additionBorderWidth : undefined,
  };
}

export function getTitlePosition(value: unknown): TextFieldTitlePosition {
  return value === 'beside' ? 'beside' : 'above';
}

export function getRequiredLabelPosition(
  value: unknown
): TextFieldSlotPosition {
  return value === 'above_input' ? 'above_input' : 'below_input';
}
